import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useConvexAuth } from 'convex/react'
import { IconUsersOutline18 } from 'nucleo-ui-outline-18'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'
import { BrandLoadingScreen } from '~/components/brand/brand-loader'
import { Button, buttonVariants } from '~/components/ui/button'
import { Spinner } from '~/components/ui/spinner'
import { titleFor } from '~/lib/route-title'
import { cn } from '~/lib/utils'
import { api } from '~convex/_generated/api'

export const Route = createFileRoute('/invite/$token')({
  component: RouteComponent,
  head: () => ({ meta: [{ title: titleFor(msg`Invitation`) }] }),
  // Client-only: this flow runs entirely against the Convex client.
  ssr: false,
})

/** A centered card framing every state of the accept-invitation flow. */
const InviteShell = ({ children }: { children: ReactNode }) => (
  <div className="flex min-h-svh items-center justify-center bg-canvas p-4">
    <div className="w-full max-w-sm rounded-xl bg-background p-8 text-center shadow-card">
      <div className="mx-auto mb-4 flex size-11 items-center justify-center rounded-full bg-muted">
        <IconUsersOutline18 className="size-5 text-muted-foreground" />
      </div>
      {children}
    </div>
  </div>
)

const InviteTitle = ({ children }: { children: ReactNode }) => (
  <h1 className="font-semibold text-lg tracking-tight">{children}</h1>
)

const InviteText = ({ children }: { children: ReactNode }) => (
  <p className="mt-1.5 text-muted-foreground text-sm">{children}</p>
)

function RouteComponent() {
  const { token } = Route.useParams()
  const { t } = useLingui()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth()
  const { data: invitation, isPending } = useQuery(
    convexQuery(api.invitations.getByToken, { token })
  )
  const { mutateAsync: accept } = useMutation({
    mutationFn: useConvexMutation(api.invitations.accept),
  })
  const [joining, setJoining] = useState(false)

  if (isPending || authLoading) {
    return <BrandLoadingScreen />
  }

  if (invitation === undefined || invitation.status === 'not_found') {
    return (
      <InviteShell>
        <InviteTitle>
          <Trans>Invitation not found</Trans>
        </InviteTitle>
        <InviteText>
          <Trans>This invitation link is not valid.</Trans>
        </InviteText>
      </InviteShell>
    )
  }

  if (invitation.status === 'revoked') {
    return (
      <InviteShell>
        <InviteTitle>
          <Trans>Invitation revoked</Trans>
        </InviteTitle>
        <InviteText>
          <Trans>This invitation is no longer valid.</Trans>
        </InviteText>
      </InviteShell>
    )
  }

  if (invitation.status === 'expired') {
    return (
      <InviteShell>
        <InviteTitle>
          <Trans>Invitation expired</Trans>
        </InviteTitle>
        <InviteText>
          <Trans>Ask for a new invitation to join this workspace.</Trans>
        </InviteText>
      </InviteShell>
    )
  }

  const workspaceName = invitation.workspaceName || t`a workspace`

  if (invitation.status === 'accepted') {
    return (
      <InviteShell>
        <InviteTitle>
          <Trans>Invitation already accepted</Trans>
        </InviteTitle>
        <InviteText>
          <Trans>You have already joined {workspaceName}.</Trans>
        </InviteText>
        <Link
          className={cn(buttonVariants(), 'mt-5 w-full')}
          to={isAuthenticated ? '/home' : '/sign-in'}
        >
          {isAuthenticated ? <Trans>Go to workspace</Trans> : <Trans>Sign in</Trans>}
        </Link>
      </InviteShell>
    )
  }

  // From here the invitation is pending.
  const inviteHref = `/invite/${token}`

  if (!isAuthenticated) {
    return (
      <InviteShell>
        <InviteTitle>
          <Trans>Join {workspaceName}</Trans>
        </InviteTitle>
        <InviteText>
          <Trans>
            Sign in or create an account with {invitation.email} to accept this invitation.
          </Trans>
        </InviteText>
        <div className="mt-5 flex flex-col gap-2">
          <Link
            className={cn(buttonVariants(), 'w-full')}
            search={{ redirect: inviteHref }}
            to="/sign-up"
          >
            <Trans>Create account</Trans>
          </Link>
          <Link
            className={cn(buttonVariants({ variant: 'secondary' }), 'w-full')}
            search={{ redirect: inviteHref }}
            to="/sign-in"
          >
            <Trans>Sign in</Trans>
          </Link>
        </div>
      </InviteShell>
    )
  }

  const emailMatches =
    invitation.viewerEmail !== null && invitation.viewerEmail.toLowerCase() === invitation.email

  if (!emailMatches) {
    return (
      <InviteShell>
        <InviteTitle>
          <Trans>Wrong account</Trans>
        </InviteTitle>
        <InviteText>
          <Trans>
            This invitation was sent to {invitation.email}, but you are signed in as a different
            account. Sign in with the invited email to continue.
          </Trans>
        </InviteText>
        <Link className={cn(buttonVariants({ variant: 'secondary' }), 'mt-5 w-full')} to="/sign-in">
          <Trans>Switch account</Trans>
        </Link>
      </InviteShell>
    )
  }

  const handleAccept = async () => {
    setJoining(true)
    try {
      const { slug } = await accept({ token })
      toast.success(t`Welcome to ${workspaceName}.`)
      // A null slug only exists during the brief backfill window — fall back
      // to the `/home` resolver, which forwards once the slug lands.
      if (slug === null) {
        await navigate({ to: '/home' })
      } else {
        await navigate({ params: { workspaceSlug: slug }, to: '/$workspaceSlug/home' })
      }
    } catch (error) {
      console.error('Accept invitation error:', error)
      toast.error(t`Could not accept the invitation.`)
      setJoining(false)
    }
  }

  return (
    <InviteShell>
      <InviteTitle>
        <Trans>Join {workspaceName}</Trans>
      </InviteTitle>
      <InviteText>
        <Trans>You have been invited to collaborate in this workspace.</Trans>
      </InviteText>
      <Button className="mt-5 w-full" disabled={joining} onClick={handleAccept} type="button">
        {joining ? <Spinner className="size-4 text-current" /> : <Trans>Accept invitation</Trans>}
      </Button>
    </InviteShell>
  )
}
